import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom";

import { useSincronizarEntreAbas } from "@/lib/sincronia";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "react-hot-toast";

import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { Layout } from "@/components/Layout";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { PWAUpdatePrompt } from "@/components/PWAUpdatePrompt";

import { ForgotPasswordPage } from "@/pages/ForgotPassword";
import { LoginPage } from "@/pages/Login";
import { NotFoundPage } from "@/pages/NotFound";
import { ResetPasswordPage } from "@/pages/ResetPassword";
import { DashboardPage } from "@/pages/Dashboard";
import { EquipmentsPage } from "@/pages/Equipments";
import { NotificacoesPage } from "@/pages/Notificacoes";
import { OcorrenciasPage } from "@/pages/Ocorrencias";
import { EquipmentDetailPage } from "@/pages/EquipmentDetail";
import SiteKioskPage from "@/pages/SiteKiosk";
import PlantaEditorPage from "@/pages/PlantaEditor";
import KioskTenantPage from "@/pages/KioskTenant";
import { AdminTenantsPage } from "@/pages/admin/Tenants";
import { TenantEditPage } from "@/pages/admin/TenantEdit";
import { AdminSitesPage } from "@/pages/admin/Sites";
import { AdminEquipmentsPage } from "@/pages/admin/Equipments";
import { AdminDevicesPage } from "@/pages/admin/Devices";
import { AdminUsersPage } from "@/pages/admin/Users";
import { AdminAlertsPage } from "@/pages/admin/Alerts";
import { AdminSettingsPage } from "@/pages/admin/Settings";
import { ReportsGeneratePage } from "@/pages/reports/ReportsGenerate";
import { ReportsLibraryPage } from "@/pages/reports/ReportsLibrary";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      retry: 1,
    },
  },
});

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * O botão "abrir" do alerta no WhatsApp monta /equipments/{{7}}. Alerta sem
 * equipamento (gateway no 4G, cliente sem telemetria) leva o marcador
 * `painel` — o modelo da Meta não aceita variável vazia nem caractere fora
 * de URL. Qualquer id que não é de ativo vai pro painel geral, em vez de
 * "ativo não encontrado".
 */
function AtivoOuPainel() {
  const { id } = useParams<{ id: string }>();
  if (!id || !UUID.test(id)) return <Navigate to="/" replace />;
  return <EquipmentDetailPage />;
}

export function App() {
  // Uma aba avisa as outras quando grava. Ver `lib/sincronia` — o editor de
  // plantas em aba própria foi o que tornou isso necessário.
  useSincronizarEntreAbas(queryClient);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeProvider>
        <AuthProvider>
          {/* #169 PWA — banner de atualização quando SW detecta versão nova */}
          <PWAUpdatePrompt />
          {/* Posição e safe-area vivem no CSS (`.toaster-envpulse` em
              index.css): o container é um portal `position: fixed` fora da
              árvore do Layout, e precisa de media query pra descer abaixo da
              TopBar no celular — coisa que estilo inline não faz. */}
          <Toaster
            position="top-center"
            containerClassName="toaster-envpulse"
            toastOptions={{
              // ! prefix override do react-hot-toast (que injeta bg/color inline).
              // dark: também precisa de ! pra vencer o inline style em dark mode.
              className:
                "!rounded-lg !bg-white dark:!bg-slate-900 !text-slate-800 dark:!text-slate-200 !shadow-card-hover !border !border-surface-200 dark:!border-slate-800",
              duration: 4000,
              // Erro fica mais tempo. Uma falha de rede em 4s some antes de
              // alguém terminar de ler o que deu errado — e aí a pessoa só
              // sabe que "não funcionou".
              error: { duration: 7000 },
            }}
          />

          <Routes>
            {/* Públicas */}
            <Route path="/login"            element={<LoginPage />} />
            <Route path="/forgot-password"  element={<ForgotPasswordPage />} />
            <Route path="/reset-password"   element={<ResetPasswordPage />} />

            {/* Protegidas */}
            <Route
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index                  element={<DashboardPage />} />
              <Route path="equipments"      element={<EquipmentsPage />} />
              {/* Preferência pessoal: sem gate de role — viewer também tem celular. */}
              <Route path="notificacoes"    element={<NotificacoesPage />} />
              <Route path="ocorrencias"     element={<OcorrenciasPage />} />
              <Route path="equipments/:id"  element={<AtivoOuPainel />} />
              {/* A tela da unidade saiu: ela repetia o dashboard do cliente
                  com menos dado. O que sobrou dela e vale por si é o
                  EDITOR, que precisa de espaço, e o PAINEL, que é outra
                  coisa. */}
              <Route path="sites/:siteId/planta" element={<PlantaEditorPage />} />

              {/* Reports — qualquer user logado */}
              <Route path="reports"          element={<Navigate to="/reports/library" replace />} />
              <Route path="reports/generate" element={<ProtectedRoute role="operator"><ReportsGeneratePage /></ProtectedRoute>} />
              <Route path="reports/library"  element={<ReportsLibraryPage />} />

              {/* Admin de tenant (admin/super_admin) */}
              <Route path="admin/sites"      element={<ProtectedRoute role="admin"><AdminSitesPage /></ProtectedRoute>} />
              <Route path="admin/equipments" element={<ProtectedRoute role="admin"><AdminEquipmentsPage /></ProtectedRoute>} />
              <Route path="admin/devices"    element={<ProtectedRoute role="admin"><AdminDevicesPage /></ProtectedRoute>} />
              <Route path="admin/users"      element={<ProtectedRoute role="admin"><AdminUsersPage /></ProtectedRoute>} />
              <Route path="admin/alerts"     element={<ProtectedRoute role="admin"><AdminAlertsPage /></ProtectedRoute>} />
              <Route path="admin/settings"   element={<ProtectedRoute role="admin"><AdminSettingsPage /></ProtectedRoute>} />

              {/* Staff envpulse — só super_admin vê a LISTA de tenants */}
              <Route path="admin/tenants"          element={<ProtectedRoute roles={["super_admin"]}><AdminTenantsPage /></ProtectedRoute>} />
              {/* Edit liberado pra qualquer admin — backend bloqueia se não for o próprio tenant */}
              <Route path="admin/tenants/:id/edit" element={<ProtectedRoute role="admin"><TenantEditPage /></ProtectedRoute>} />
            </Route>

            {/* Modo painel — protegido, mas sem o chrome do app. Fora do
                Layout de propósito: numa tela pendurada na parede, menu e
                barra lateral são só maneiras de alguém deixar o painel num
                estado do qual ninguém vai tirar. */}
            <Route
              path="/kiosk/sites/:siteId"
              element={<ProtectedRoute><SiteKioskPage /></ProtectedRoute>}
            />
            <Route
              path="/kiosk/tenants/:tenantId"
              element={<ProtectedRoute><KioskTenantPage /></ProtectedRoute>}
            />

            {/* Catch-all */}
            <Route path="/404" element={<NotFoundPage />} />
            <Route path="*" element={<Navigate to="/404" replace />} />
          </Routes>
        </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
